import * as React from 'react';

import { debugLog, isAssistantPageActive } from '../../lib/assistant/connectionLifecycle';

/** A ref whose `current` this hook reads. Spelled locally to stay off React's shifting ref type names. */
type MutableRef<T> = { current: T };

/** Initial backoff before the first auto-reconnect attempt after an unexpected close. */
const RECONNECT_INITIAL_DELAY_MS = 1000;

/**
 * Cap on the exponential backoff between auto-reconnect attempts. The schedule
 * is 1s → 2s → 4s → 8s → 16s → 30s → 30s … so the cap kicks in on attempt 6.
 */
const RECONNECT_MAX_DELAY_MS = 30_000;

/** What {@link useAssistantReconnect} needs from the socket hook to decide when to dial. */
export interface IAssistantReconnectDeps {
  /** Gates the recovery listeners entirely — a logged-out session registers none. */
  isAuthenticated: boolean;
  /** Read to skip a dial while a connection is already OPEN or CONNECTING. */
  wsRef: MutableRef<WebSocket | null>;
  /** A superseded tab reconnects only on an explicit reclaim, never on a recovery signal. */
  supersededRef: MutableRef<boolean>;
  /** Dials a fresh connection. Must be referentially stable. */
  connect: () => void;
}

/** The reconnect controls the socket hook's close handler drives. */
export interface IAssistantReconnect {
  /** Arm the next backoff attempt, or record that one is owed if the page can't take it. */
  scheduleReconnect: () => void;
  /** Record that a reconnect is owed but withheld, so the next recovery signal picks it up. */
  deferReconnect: () => void;
  /** Restart the backoff schedule from its initial delay. */
  resetBackoff: () => void;
  /** Drop the owed-reconnect flag — the connection is live, so nothing is outstanding. */
  clearDeferred: () => void;
  /**
   * Disarm any armed backoff timer. Teardown must call this *after* closing the
   * socket: a socket closed while CONNECTING is reported 1006, which schedules a
   * retry, and a timer armed after this hook's own cleanup has nothing left to
   * clear it.
   */
  cancelScheduledReconnect: () => void;
}

/**
 * The reconnect schedule and the browser signals that short-circuit it.
 *
 * Split from the socket hook because none of it touches turn or transcript
 * state: it decides only *when* to dial, and calls the `connect` it was handed.
 * The socket hook still owns the close handler that decides *whether* a given
 * close is worth reconnecting from.
 *
 * Registers the `online` / `offline` / `focus` / `visibilitychange` listeners as
 * a side effect for as long as `deps.isAuthenticated` holds.
 *
 * @param deps - see {@link IAssistantReconnectDeps}; `deps.connect` must be
 * referentially stable, since the listener effect closes over it.
 * @returns the controls in {@link IAssistantReconnect}, each stable across renders.
 */
export function useAssistantReconnect(deps: IAssistantReconnectDeps): IAssistantReconnect {
  const { isAuthenticated, wsRef, supersededRef, connect } = deps;

  const reconnectTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);
  // Drives `scheduleReconnect`'s backoff delay; resets on `ws.onopen` and on
  // any immediate-recovery event (online / focus / visibility).
  const reconnectAttemptsRef = React.useRef(0);
  // True when an auto-reconnect is owed but withheld because the page is
  // inactive or the browser is offline. Cleared the moment a recovery signal
  // (online / focus / visibility) actually dials. The recovery listeners read it
  // to tell "owed a reconnect" apart from "nothing to do".
  const deferredReconnectRef = React.useRef(false);

  const isCurrentPageActive = (): boolean => isAssistantPageActive(document);

  const scheduleReconnect = React.useCallback((): void => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (!navigator.onLine) {
      debugLog('scheduleReconnect skipped — browser is offline; waiting for `online` event');
      deferredReconnectRef.current = true;
      return;
    }
    if (!isCurrentPageActive()) {
      debugLog('scheduleReconnect deferred — page is not active');
      deferredReconnectRef.current = true;
      return;
    }
    const attempt = reconnectAttemptsRef.current;
    reconnectAttemptsRef.current = attempt + 1;
    const delay = Math.min(RECONNECT_INITIAL_DELAY_MS * 2 ** attempt, RECONNECT_MAX_DELAY_MS);
    debugLog('scheduleReconnect armed', { attempt: attempt + 1, delayMs: delay });
    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectTimeoutRef.current = null;
      if (!isCurrentPageActive()) {
        debugLog('scheduled reconnect deferred — page became inactive before retry');
        deferredReconnectRef.current = true;
        return;
      }
      connect();
    }, delay);
  }, [connect]);

  const deferReconnect = React.useCallback((): void => {
    deferredReconnectRef.current = true;
  }, []);

  const resetBackoff = React.useCallback((): void => {
    reconnectAttemptsRef.current = 0;
  }, []);

  const clearDeferred = React.useCallback((): void => {
    deferredReconnectRef.current = false;
  }, []);

  const cancelScheduledReconnect = React.useCallback((): void => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  }, []);

  React.useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    let cancelled = false;

    // Shared recovery path for the three "a page/network signal returned" events.
    // `online` dials whenever the socket is dead (`requireDeferred = false`);
    // `focus`/`visibilitychange` act only when a reconnect was actually deferred
    // (`requireDeferred = true`), so an incidental focus on a healthy tab is a
    // no-op. All three then share the same guards: skip if the WS is already
    // live, re-defer if the browser is offline or the page is inactive (an
    // `online` event can fire spuriously while still offline on some platforms),
    // otherwise reset the backoff and dial immediately. The offline/inactive
    // branches set `deferredReconnectRef` so the next signal retries; on the
    // `requireDeferred` path that flag is already true, so the write is a no-op.
    const attemptImmediateReconnect = (
      source: 'online' | 'focus' | 'visibilitychange',
      requireDeferred: boolean
    ): void => {
      if (cancelled) {
        debugLog(`${source} event ignored — effect already cleaned up`);
        return;
      }
      // A superseded tab reconnects ONLY via an explicit Reconnect click
      // (`reclaim` clears this flag), never on an incidental online/focus/
      // visibility signal — auto-dialing would silently re-claim the thread.
      if (supersededRef.current) {
        debugLog(`${source} event ignored — superseded, awaiting explicit reconnect`);
        return;
      }
      const ws = wsRef.current;
      const readyState = ws?.readyState;
      if (ws && (readyState === WebSocket.OPEN || readyState === WebSocket.CONNECTING)) {
        debugLog(`${source} event — no-op (WS already live)`, { readyState });
        return;
      }
      if (requireDeferred && !deferredReconnectRef.current) {
        debugLog(`${source} event — no deferred reconnect pending`, { readyState: readyState ?? 'no-ws' });
        return;
      }
      if (!navigator.onLine) {
        debugLog(`${source} event — browser is offline; keeping reconnect deferred`);
        deferredReconnectRef.current = true;
        return;
      }
      if (!isCurrentPageActive()) {
        debugLog(`${source} event — page is not active; keeping reconnect deferred`);
        deferredReconnectRef.current = true;
        return;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      reconnectAttemptsRef.current = 0;
      deferredReconnectRef.current = false;
      debugLog(`${source} event — reconnecting immediately`, { readyState: readyState ?? 'no-ws' });
      connect();
    };

    // Recovery skips the backoff timer when the browser hands us a real signal.
    const handleOnline = (): void => {
      attemptImmediateReconnect('online', false);
    };
    const handleFocus = (): void => {
      attemptImmediateReconnect('focus', true);
    };
    const handleVisibilityChange = (): void => {
      attemptImmediateReconnect('visibilitychange', true);
    };
    // Browsers (and DevTools "Offline" mode) don't necessarily close an idle
    // WebSocket when the network drops — the socket sits at `readyState OPEN`
    // until the next write fails, which never happens for a receive-only
    // connection. Close it proactively so `onclose` runs and the UI status
    // flips to disconnected; `handleOnline` reconnects if the page is active
    // when the network returns, otherwise focus/visibility recovery handles it.
    // Use code 1000 so the close handler skips its own reconnect-scheduling
    // branch — `handleOnline` is the recovery path here.
    const handleOffline = (): void => {
      debugLog('offline event received');
      const ws = wsRef.current;
      if (ws && (ws.readyState === WebSocket.OPEN || ws.readyState === WebSocket.CONNECTING)) {
        ws.close(1000, 'Browser reports offline');
      }
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      cancelled = true;
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, [isAuthenticated, wsRef, supersededRef, connect]);

  return { scheduleReconnect, deferReconnect, resetBackoff, clearDeferred, cancelScheduledReconnect };
}
